import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Chip,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { Add, Delete, Edit as EditIcon, EventBusy, Close } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import { FooterInternal } from "../components/Footer";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/useToast";
import { Toast } from "../components/Toast";
import SavingOverlay from "../components/SavingOverlay";
import {
  entityTaxRegimeService,
  TaxRegimeItem,
  REGIME_OPTIONS,
} from "../services/entityTaxRegimeService";
import { colors, typography } from "../theme/designSystem";
import ResultCount from "../components/ResultCount";

const formatDate = (value?: string) =>
  value ? new Date(`${value.substring(0, 10)}T00:00:00`).toLocaleDateString("pt-BR") : "-";

// Regime tributário com vigência (RN-206a): um vigente por entidade; trocar =
// inativar com data de corte + criar novo com data de início. As taxas são
// definidas na criação — alterar imposto é criar novo regime (histórico intacto).
const EntityTaxRegimes: React.FC = () => {
  const { navigateTo, getAllParams } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();

  const params = getAllParams();
  const entityId = params.get("entityId") || user?.entityId || "";
  const entityName = params.get("entityName") || user?.entityName || "Entidade";

  const [regimes, setRegimes] = useState<TaxRegimeItem[]>([]);
  const [search, setSearch] = useState("");
  const [situation, setSituation] = useState<"todas" | "vigentes" | "encerradas">("todas");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [regime, setRegime] = useState(REGIME_OPTIONS[3]);
  const [startDate, setStartDate] = useState("");
  const [newEndDate, setNewEndDate] = useState("");
  const [rates, setRates] = useState<Array<{ name: string; percent: string }>>([
    { name: "", percent: "" },
  ]);

  const [toDeactivate, setToDeactivate] = useState<TaxRegimeItem | null>(null);
  const [endDate, setEndDate] = useState("");

  const [toEdit, setToEdit] = useState<TaxRegimeItem | null>(null);
  const [editStart, setEditStart] = useState("");
  const [editEnd, setEditEnd] = useState("");

  const load = async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const data = await entityTaxRegimeService.getByEntity(entityId);
      setRegimes(data);
    } catch (error) {
      console.error("Erro ao carregar regimes:", error);
      showToast("Erro ao carregar os regimes tributários.", "error");
    } finally {
      setLoading(false);
    }
  };

  /** Minusculas e sem acento: quem busca "presumido" espera achar "Presumido". */
  const normalize = (v: string) =>
    v.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

  const filtered = regimes.filter((item) => {
    if (situation !== "todas" && item.active !== (situation === "vigentes")) {
      return false;
    }
    return !search.trim() || normalize(item.regime || "").includes(normalize(search));
  });

  const clearFilters = () => {
    setSearch("");
    setSituation("todas");
  };

  const hasFilterChanges = search !== "" || situation !== "todas";

  useEffect(() => {
    document.title = `${entityName} / Regime Tributário`;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const openCreate = () => {
    setRegime(REGIME_OPTIONS[3]);
    setStartDate("");
    setNewEndDate("");
    setRates([{ name: "", percent: "" }]);
    setIsCreateOpen(true);
  };

  const handleCreate = async () => {
    if (saving) return;
    if (!startDate) {
      showToast("Informe a data de início da vigência.", "error");
      return;
    }
    const parsedRates = rates
      .filter((r) => r.name.trim())
      .map((r) => ({
        name: r.name.trim(),
        ratePercent: Number(r.percent.replace(",", ".")) || 0,
      }));
    if (parsedRates.length === 0) {
      showToast("Inclua ao menos um tipo de imposto com percentual.", "error");
      return;
    }
    setSaving(true);
    try {
      await entityTaxRegimeService.create({
        entityId,
        regime,
        startDate,
        endDate: newEndDate || undefined,
        rates: parsedRates,
      });
      showToast("Regime incluído com sucesso.", "success");
      setIsCreateOpen(false);
      await load();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { description?: string } } })?.response
          ?.data?.description || "Erro ao incluir o regime.";
      showToast(message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!toDeactivate || saving) return;
    if (!endDate) {
      showToast("Informe a data de corte (fim da vigência).", "error");
      return;
    }
    setSaving(true);
    try {
      await entityTaxRegimeService.deactivate(toDeactivate.id, endDate);
      showToast("Regime inativado — data de corte registrada.", "success");
      setToDeactivate(null);
      setEndDate("");
      await load();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { description?: string } } })?.response
          ?.data?.description || "Erro ao inativar o regime.";
      showToast(message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePeriod = async () => {
    if (!toEdit || saving) return;
    if (!editStart) {
      showToast("Informe a data de início da vigência.", "error");
      return;
    }
    setSaving(true);
    try {
      await entityTaxRegimeService.updatePeriod(toEdit.id, editStart, editEnd || undefined);
      showToast("Vigência atualizada.", "success");
      setToEdit(null);
      await load();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { description?: string } } })?.response
          ?.data?.description || "Erro ao atualizar a vigência.";
      showToast(message, "error");
    } finally {
      setSaving(false);
    }
  };

  const dateFieldSx = {
    "& .MuiOutlinedInput-root": {
      height: "40px",
      "& fieldset": { borderColor: colors.border },
      "&:hover fieldset": { borderColor: colors.border },
      "&.Mui-focused fieldset": { borderColor: colors.primary },
    },
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters>
          <div className="c4u-page-header c4u-page-header--in-panel">
            <div className="c4u-page-header__texts">
              <h1 className="c4u-page-header__title">{entityName} / Regime Tributário</h1>
              <p className="c4u-page-header__subtitle">Vigências de regime e tipos de imposto — os relatórios calculam os impostos pelo regime vigente em cada competência.</p>
            </div>
            <div className="c4u-page-header__actions">
              <FaqButton />
            </div>
          </div>

          <div className="c4u-panel">
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields">
                <TextField
                  size="small"
                  label="Busca por regime"
                  placeholder="Buscar"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  inputProps={{ maxLength: 80 }}
                />
                <TextField
                  select
                  size="small"
                  label="Situação"
                  value={situation}
                  onChange={(e) =>
                    setSituation(e.target.value as "todas" | "vigentes" | "encerradas")
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                >
                  <MenuItem value="todas">Todas</MenuItem>
                  <MenuItem value="vigentes">Vigentes</MenuItem>
                  <MenuItem value="encerradas">Encerradas</MenuItem>
                </TextField>
              </div>

              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges} />
                <AddButton onClick={openCreate} title="Incluir regime" />
              </div>
            </div>

          <ResultCount shown={filtered.length} total={regimes.length} />
          <Box className="admin-plans-list-container" sx={{ background: "none", border: "none", borderRadius: 0, boxShadow: "none" }}>
            <Box className="admin-plans-table admin-entities-table">
              <Box
                className="c4u-table__header">
                <Box className="admin-plans-header-cell" sx={{ flex: "0 0 210px", textAlign: "left" }}>
                  Regime
                </Box>
                <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 110px", textAlign: "left" }}>
                  Início
                </Box>
                <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 0 110px", textAlign: "left" }}>
                  Fim
                </Box>
                <Box className="admin-plans-header-cell c4u-col--xl" sx={{ flex: "1 1 auto", minWidth: 200, textAlign: "left" }}>
                  Impostos
                </Box>
                <Box className="admin-plans-header-cell" sx={{ flex: "0 0 90px", textAlign: "left" }}>
                  Total
                </Box>
                <Box className="admin-plans-header-cell" sx={{ flex: "0 0 90px", textAlign: "left" }}>
                  Status
                </Box>
                <Box className="admin-plans-header-cell" sx={{ flex: "0 0 130px", justifyContent: "flex-end" }}>
                  Ações
                </Box>
              </Box>

              <Box className="admin-plans-table-body">
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress size={28} sx={{ color: colors.primary }} />
                  </Box>
                ) : regimes.length === 0 ? (
                  <Box sx={{ py: 6, textAlign: "center" }}>
                    <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                      Nenhum regime cadastrado — inclua o regime vigente com a data de início.
                    </Typography>
                  </Box>
                ) : (
                  filtered.map((item) => (
                    <Box
                      key={item.id}
                      className="c4u-table__row"
                      onClick={() => {
                        setEditStart(item.startDate.substring(0, 10));
                        setEditEnd(item.endDate ? item.endDate.substring(0, 10) : "");
                        setToEdit(item);
                      }}
                    >
                      <Box
                        className="admin-plans-cell"
                        sx={{ flex: "0 0 210px", textAlign: "left", fontWeight: 600 }}
                        data-label="Regime"
                      >
                        {item.regime}
                      </Box>
                      <Box className="admin-plans-cell c4u-col--md" sx={{ flex: "0 0 110px", textAlign: "left" }} data-label="Início">
                        {formatDate(item.startDate)}
                      </Box>
                      <Box className="admin-plans-cell c4u-col--lg" sx={{ flex: "0 0 110px", textAlign: "left" }} data-label="Fim">
                        {item.endDate ? formatDate(item.endDate) : "vigente"}
                      </Box>
                      <Box
                        className="admin-plans-cell c4u-col--xl"
                        sx={{ flex: "1 1 auto", minWidth: 200, textAlign: "left", flexWrap: "wrap", gap: 0.5 }}
                        data-label="Impostos"
                      >
                        {item.rates
                          .filter((r) => r.active)
                          .map((r) => (
                            <Chip
                              key={r.id}
                              label={`${r.name} ${r.ratePercent.toLocaleString("pt-BR")}%`}
                              size="small"
                              sx={{ mr: 0.5, backgroundColor: "#e9ecef" }}
                            />
                          ))}
                      </Box>
                      <Box className="admin-plans-cell" sx={{ flex: "0 0 90px", textAlign: "left", fontWeight: 600 }} data-label="Total">
                        {item.totalPercent.toLocaleString("pt-BR")}%
                      </Box>
                      <Box className="admin-plans-cell" sx={{ flex: "0 0 90px", textAlign: "left" }} data-label="Status">
                        <Chip
                          label={item.active ? "Vigente" : "Encerrado"}
                          size="small"
                          sx={{
                            color: "white",
                            backgroundColor: item.active ? colors.success : "#adb5bd",
                          }}
                        />
                      </Box>
                      <Box
                        className="admin-plans-cell admin-plans-actions"
                        sx={{ flex: "0 0 130px", textAlign: "right", display: "flex", gap: 0.5, justifyContent: "flex-end" }}
                        data-label="Ações"
                      >
                        <Tooltip title="Editar vigência">
                          <IconButton
                            className="c4u-row-action"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditStart(item.startDate.substring(0, 10));
                              setEditEnd(item.endDate ? item.endDate.substring(0, 10) : "");
                              setToEdit(item);
                            }}
                            sx={{
                              backgroundColor: "#17a2b8",
                              color: "white",
                              "&:hover": { backgroundColor: "#138496" },
                            }}
                          >
                            <EditIcon sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        </Tooltip>
                        {item.active && (
                          <Tooltip title="Inativar (definir data de corte)">
                            <IconButton
                              className="c4u-row-action"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEndDate("");
                                setToDeactivate(item);
                              }}
                              sx={{
                                backgroundColor: "#dc3545",
                                color: "white",
                                "&:hover": { backgroundColor: "#c82333" },
                              }}
                            >
                              <EventBusy sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          </Box>
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />

      {/* Modal de inclusão */}
      <Dialog
        open={isCreateOpen}
        onClose={() => !saving && setIsCreateOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}
      >
        <SavingOverlay open={saving} message="Salvando..." />
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: "white",
            fontWeight: 600,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Novo regime tributário
          <IconButton onClick={() => setIsCreateOpen(false)} disabled={saving} sx={{ color: "white" }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: "1.5rem", pt: "1.5rem !important" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              select
              label="Regime"
              value={regime}
              onChange={(e) => setRegime(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            >
              {REGIME_OPTIONS.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Início da vigência"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={dateFieldSx}
              helperText="Os impostos passam a ser calculados por este regime a partir desta competência"
            />
            <TextField
              label="Fim da vigência (opcional)"
              type="date"
              value={newEndDate}
              onChange={(e) => setNewEndDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={dateFieldSx}
              helperText="Em branco = vigente. Períodos de regimes não podem se cruzar; sem regime na competência, os relatórios calculam sem o percentual"
            />
            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.textSecondary }}>
              Tipos de imposto (% sobre o bruto recebido)
            </Typography>
            {rates.map((rate, index) => (
              <Box key={index} sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1 }}>
                <TextField
                  label="Imposto"
                  value={rate.name}
                  onChange={(e) =>
                    setRates((rs) => rs.map((r, i) => (i === index ? { ...r, name: e.target.value } : r)))
                  }
                  inputProps={{ maxLength: 30 }}
                  placeholder="ISS, PIS, COFINS..."
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="%"
                  value={rate.percent}
                  onChange={(e) =>
                    setRates((rs) =>
                      rs.map((r, i) =>
                        i === index ? { ...r, percent: e.target.value.replace(/[^\d.,]/g, "") } : r
                      )
                    )
                  }
                  inputProps={{ maxLength: 6, inputMode: "decimal" }}
                  sx={{ width: { xs: "100%", sm: 120 } }}
                  InputLabelProps={{ shrink: true }}
                />
                <IconButton
                  onClick={() => setRates((rs) => rs.filter((_, i) => i !== index))}
                  disabled={rates.length === 1}
                  title="Remover imposto"
                  sx={{ alignSelf: { xs: "flex-end", sm: "center" } }}
                >
                  <Delete />
                </IconButton>
              </Box>
            ))}
            <Button
              startIcon={<Add />}
              onClick={() => setRates((rs) => [...rs, { name: "", percent: "" }])}
              sx={{ alignSelf: "flex-start", textTransform: "none", color: colors.primary }}
            >
              Adicionar imposto
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: "1rem 1.5rem" }}>
          <Button onClick={() => setIsCreateOpen(false)} disabled={saving} variant="outlined" sx={{ textTransform: "none" }}>
            Cancelar
          </Button>
          <Button
            onClick={handleCreate}
            disabled={saving}
            variant="contained"
            sx={{ textTransform: "none", backgroundColor: colors.primary }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de inativação com data de corte */}
      <Dialog
        open={!!toDeactivate}
        onClose={() => !saving && setToDeactivate(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}
      >
        <SavingOverlay open={saving} message="Salvando..." />
        <DialogTitle sx={{ backgroundColor: "#dc3545", color: "white", fontWeight: 600 }}>
          Inativar regime
        </DialogTitle>
        <DialogContent sx={{ p: "1.5rem", pt: "1.5rem !important" }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Informe a <strong>data de corte</strong> — último dia em que o regime{" "}
            <strong>{toDeactivate?.regime}</strong> vale para o cálculo dos impostos. A
            partir da competência seguinte, vale o próximo regime cadastrado.
          </Typography>
          <TextField
            label="Fim da vigência (data de corte)"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            required
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={dateFieldSx}
          />
        </DialogContent>
        <DialogActions sx={{ p: "1rem 1.5rem" }}>
          <Button onClick={() => setToDeactivate(null)} disabled={saving} variant="outlined" sx={{ textTransform: "none" }}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeactivate}
            disabled={saving}
            variant="contained"
            color="error"
            sx={{ textTransform: "none" }}
          >
            Inativar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de edição de vigência */}
      <Dialog
        open={!!toEdit}
        onClose={() => !saving && setToEdit(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}
      >
        <SavingOverlay open={saving} message="Salvando..." />
        <DialogTitle sx={{ backgroundColor: colors.primary, color: "white", fontWeight: 600 }}>
          Editar vigência — {toEdit?.regime}
        </DialogTitle>
        <DialogContent sx={{ p: "1.5rem", pt: "1.5rem !important" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Início da vigência"
              type="date"
              value={editStart}
              onChange={(e) => setEditStart(e.target.value)}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={dateFieldSx}
            />
            <TextField
              label="Fim da vigência"
              type="date"
              value={editEnd}
              onChange={(e) => setEditEnd(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={dateFieldSx}
              helperText="Em branco = vigente. Datas que cruzem outro regime são recusadas"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: "1rem 1.5rem" }}>
          <Button onClick={() => setToEdit(null)} disabled={saving} variant="outlined" sx={{ textTransform: "none" }}>
            Cancelar
          </Button>
          <Button
            onClick={handleUpdatePeriod}
            disabled={saving}
            variant="contained"
            sx={{ textTransform: "none", backgroundColor: colors.primary }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </Box>
  );
};

export default EntityTaxRegimes;
